import { kebabCase, upperFirst } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useStore } from 'react-redux';

import { TrackingCategory } from '@swf/core/enums';
import type { TrackingContextValue } from '@swf/core/providers/TrackingContext';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { WidgetContainer } from '@swf/core/types/Widget';
import { fetchProperties } from '@swf/core/utils/widgets';

const useTrackingContext = (
  widgetConfig: WidgetContainer
): [TrackingContextValue, () => Promise<void>] => {
  const {
    type,
    id: widgetId,
    meta: { name: widgetName },
    services = {},
    config
  } = widgetConfig;

  const { getState } = useStore<RootState>();

  const trackingContext = useMemo<TrackingContextValue>(() => {
    let category = upperFirst(kebabCase(type).split('-')[0]) as TrackingCategory;
    category = [TrackingCategory.Casino, TrackingCategory.Sports].includes(category)
      ? category
      : TrackingCategory.Stars;

    return {
      category,
      widget: { widgetName, widgetId }
    };
  }, [type, widgetId, widgetName]);

  const updateTrackingContext = useCallback(async () => {
    const { trackingReducer } = await fetchProperties(type);
    if (trackingReducer) {
      const widgetData = select.widgets.getData(getState(), widgetId);
      trackingContext.entities = trackingReducer(services, widgetData, config, getState);
    }
  }, [type, trackingContext, services, getState, widgetId, config]);

  useEffect(() => {
    updateTrackingContext();
  }, [updateTrackingContext]);

  return [trackingContext, updateTrackingContext];
};

export default useTrackingContext;
